import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { FaExclamationCircle } from 'react-icons/fa';
import styled from 'styled-components';

import { useMysteryBoxSelectionsContext } from '~/shared/utils/mysteryBoxSelections';
import { mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import MysteryBoxSectionDivider from '../MysteryBoxSectionDivider';

const Wrapper = styled.div`
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.mysteryBoxDividerBorder};
  padding-bottom: 8px;

  ${mobile`
    padding-top: 20px;
  `}
`;

const QuestionRow = styled.div`
  margin-bottom: 16px;
`;

const SelectorTextWrapper = styled.div`
  color: ${({ theme }) => theme.colors.mysteryBoxLabel};
  font-size: 12px;
  margin-bottom: 6px;

  ${mobile`
    padding: 0 16px;
    font-size: 14px;
  `}
`;

const SelectorLabelWrapper = styled.div`
  display: flex;
`;

const SelectedLabelText = styled.span`
  color: ${({ theme }) => theme.colors.mysteryBoxLabel};
  font-weight: 700;
  text-transform: uppercase;
`;

const SoldOutMessage = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.errorMessage};
  display: flex;
`;

const SelectSizeWarningMessage = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.errorMessage};
  display: flex;
`;

const ErrorIcon = styled(FaExclamationCircle)`
  font-size: 16px;
  font-weight: 500;
  margin-right: 4px;
`;

const SelectorList = styled.ul`
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  list-style: none;
  margin: 0;

  ${mobile`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0 16px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

const SelectorRadioButton = styled.input.attrs({ type: 'radio' })`
  opacity: 0;
  position: absolute;
`;

const SelectorItem = styled.li`
  align-items: center;
  background-color: ${({ $isDisabled, theme }) =>
    $isDisabled
      ? theme.colors.mysteryBoxDisabledSelector
      : theme.colors.mysteryBoxEnabledSelector};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.7 : 1)};
  position: relative;

  ${mobile`
    flex: 0 0 80px;
  `}
`;

const SelectorItemLabel = styled.label`
  align-items: center;
  background: ${({ $isDisabled, theme }) =>
    $isDisabled
      ? `linear-gradient(
          to top left,
          transparent calc(50% - 1px),
          ${theme.colors.soldOutSelectorItemSizeInput} calc(50% - 1px),
          ${theme.colors.soldOutSelectorItemSizeInput} calc(50% + 1px),
          transparent calc(50% + 1px),
          transparent
        )`
      : 'none'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  height: 100%;
  justify-content: center;
  text-align: center;
  width: 100%;

  ${SelectorRadioButton}:checked + & {
    border: 2px solid ${({ theme }) => theme.colors.mysteryBoxSelectionOutline};
  }
`;

export default function MysteryBoxSizeSection({ config }) {
  const { sizeSelections, selectSize, setSizeQuestions } =
    useMysteryBoxSelectionsContext();

  useEffect(() => {
    setSizeQuestions(config.sizeQuestionList);
  }, [config.sizeQuestionList, setSizeQuestions]);

  return (
    <Wrapper>
      {config.sizeSectionTitle && (
        <MysteryBoxSectionDivider>
          {config.sizeSectionTitle}
        </MysteryBoxSectionDivider>
      )}
      {config.sizeQuestionList.map((question) => {
        const questionId = String(question.mysteryBoxPreferenceTypeId);
        const visibleAnswers = question.answers.filter((a) => !a.hidden);
        const selectedLabel = sizeSelections[questionId];
        const selectedAnswer = selectedLabel
          ? question.answers.find((a) => a.label === selectedLabel)
          : null;
        const selectedIsDisabled = selectedAnswer?.disabled === true;

        return (
          <QuestionRow key={questionId}>
            <SelectorTextWrapper>
              <SelectorLabelWrapper>
                {question.label}:&nbsp;
                {!selectedLabel ? (
                  <SelectSizeWarningMessage>
                    <ErrorIcon />
                    <FormattedMessage
                      id="site_product_page.select_a_size"
                      defaultMessage="Select a size"
                    />
                  </SelectSizeWarningMessage>
                ) : (
                  <>
                    <SelectedLabelText>{selectedLabel}</SelectedLabelText>
                    {selectedIsDisabled && (
                      <SoldOutMessage>
                        &nbsp;-&nbsp;
                        <FormattedMessage
                          id="site_product_page.out_of_stock"
                          defaultMessage="Out of Stock"
                        />
                      </SoldOutMessage>
                    )}
                  </>
                )}
              </SelectorLabelWrapper>
            </SelectorTextWrapper>
            <SelectorList>
              {visibleAnswers.map((answer) => {
                const inputId = `mystery-box-${questionId}-${answer.label.replace(
                  /\W+/g,
                  '-'
                )}`;
                const isDisabled = answer.disabled === true;
                return (
                  <SelectorItem key={answer.label} $isDisabled={isDisabled}>
                    <SelectorRadioButton
                      id={inputId}
                      name={questionId}
                      value={answer.label}
                      checked={selectedLabel === answer.label}
                      onChange={() => selectSize(questionId, answer.label)}
                    />
                    <SelectorItemLabel
                      htmlFor={inputId}
                      $isDisabled={isDisabled}
                    >
                      {answer.label}
                    </SelectorItemLabel>
                  </SelectorItem>
                );
              })}
            </SelectorList>
          </QuestionRow>
        );
      })}
    </Wrapper>
  );
}

MysteryBoxSizeSection.propTypes = {
  config: PropTypes.shape({
    sizeSectionTitle: PropTypes.string,
    sizeQuestionList: PropTypes.arrayOf(
      PropTypes.shape({
        mysteryBoxPreferenceTypeId: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
        answers: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            hidden: PropTypes.bool,
          })
        ).isRequired,
      })
    ).isRequired,
  }).isRequired,
};
